import React, { useRef, useState } from 'react'
import { Flex, Tabs, Typography } from 'antd'
import { SafetyCertificateOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import { Configuration, PreviewAppearance } from '../public-form'
import { BrandingContentControls } from './branding-content-controls'
import { BrandingStyleControls } from './branding-style-controls'
import { BrandingPreview } from './branding-preview'

type BrandingEditorProps = {
    value: Configuration
    onChange: (value: Configuration) => void
    onUpload?: (file: File) => Promise<string>
    sharing?: React.ReactNode
    publicationStatus?: React.ReactNode
}
export function BrandingEditor({ value, onChange, onUpload, sharing, publicationStatus }: BrandingEditorProps) {
    const { t } = useTranslation()
    const [previewAppearance, setPreviewAppearance] = useState<PreviewAppearance>(
        value.appearance === 'dark' ? 'dark' : 'light'
    )
    const activeAppearance = value.appearance === 'system' ? previewAppearance : value.appearance
    const [section, setSection] = useState('content')
    const [device, setDevice] = useState('Mobile')
    const latest = useRef(value)
    latest.current = value
    const set = (patch: Partial<Configuration>) => onChange({ ...latest.current, ...patch })
    const editor = (
        <div className="public-form-editor-grid">
            <div className="min-w-0">
                {section === 'content' ? (
                    <BrandingContentControls
                        value={value}
                        set={set}
                        onUpload={onUpload}
                    />
                ) : (
                    <BrandingStyleControls
                        value={value}
                        set={set}
                        previewAppearance={previewAppearance}
                        setPreviewAppearance={setPreviewAppearance}
                    />
                )}
            </div>
            <div className="min-w-0">
                <BrandingPreview
                    value={value}
                    activeAppearance={activeAppearance}
                    device={device}
                    onDeviceChange={setDevice}
                />
            </div>
        </div>
    )
    return (
        <div className="public-form-editor">
            <Tabs
                destroyOnHidden
                tabBarExtraContent={publicationStatus}
                activeKey={section}
                onChange={setSection}
                items={[
                    { key: 'content', label: t('Content'), children: editor },
                    { key: 'branding', label: t('Branding'), children: editor },
                    ...(sharing ? [{ key: 'sharing', label: t('Share'), children: sharing }] : []),
                ]}
            />
            <Flex
                gap="small"
                align="baseline"
                className="public-form-protection-note mt-4 border-t border-gray-200 pt-3"
            >
                <SafetyCertificateOutlined className="text-ink-tertiary" />
                <Typography.Text type="secondary">{t('This form uses automatic bot protection.')}</Typography.Text>
            </Flex>
        </div>
    )
}
